import React, { Fragment } from 'react';
import { ApplicationState, AppState } from '../../../store';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { Box, Button, Grid } from '@material-ui/core';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import {
  OrganizationBuyoutConfig,
  SaveOrganizationBuyoutConfigResponse
} from '../../../interfaces/Organization';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { withSnackbar, ProviderContext } from 'notistack';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { WrapperSimple } from '../../../layout-components';
import { ScaleLoader } from 'react-spinners';
import NumberFormat from 'react-number-format';

interface IOrganizationBuyoutConfigState {
  organizationBuyoutConfig: OrganizationBuyoutConfig;
  isLoading: boolean;
  isSaving: boolean;
}

type IOrganizationBuyoutConfigProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;

class OrganizationBuyoutConfigTab extends React.PureComponent<
  IOrganizationBuyoutConfigProps,
  IOrganizationBuyoutConfigState
> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;

  static contextType = ApplicationContext;

  private get organizationId(): number {
    return Number.parseInt(this.props.match.params.id);
  }

  private emptyConfig = (): OrganizationBuyoutConfig => ({
    id: 0,
    organizationId: this.organizationId,
    conversionCoefficient: 0,
    maxEligibleDamageAmount: undefined,
    dateCreation: '',
    dateModification: '',
    createdBy: '',
    modifiedBy: ''
  });

  state = {
    organizationBuyoutConfig: this.emptyConfig(),
    isLoading: true,
    isSaving: false
  } as IOrganizationBuyoutConfigState;

  public componentDidMount() {
    this.setState({ isLoading: true }, async () => {
      await this.loadData();
    });
  }

  loadData = async () => {
    try {
      const config = await this.organizationService.GetOrganizationBuyoutConfig(
        this.organizationId
      );
      this.setState({ organizationBuyoutConfig: config, isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  handleConversionCoefficientChange = (value: string) => {
    this.setState({
      organizationBuyoutConfig: {
        ...this.state.organizationBuyoutConfig,
        conversionCoefficient: value === '' ? 0 : parseFloat(value)
      }
    });
  };

  handleMaxEligibleDamageAmountChange = (value: string) => {
    this.setState({
      organizationBuyoutConfig: {
        ...this.state.organizationBuyoutConfig,
        maxEligibleDamageAmount: value === '' ? undefined : parseFloat(value)
      }
    });
  };

  saveConfig = async () => {
    const request: OrganizationBuyoutConfig = {
      ...this.state.organizationBuyoutConfig,
      organizationId: this.organizationId
    };

    try {
      this.setState({ isSaving: true });
      const response: SaveOrganizationBuyoutConfigResponse =
        await this.organizationService.SaveOrganizationBuyoutConfig(request);

      if (!response.success) {
        this.setState({ isSaving: false });
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(response.message, response.message),
          { variant: 'error' }
        );
        return;
      }

      this.setState({
        organizationBuyoutConfig: response.organizationBuyoutConfig,
        isSaving: false
      });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.setState({ isSaving: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  CoefficientFormatCustom = (props: any) => {
    const { inputRef, onChange, ...other } = props;
    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef}
        onValueChange={(values: any) => {
          onChange({ target: { value: values.value } });
        }}
        allowNegative={false}
        thousandSeparator={false}
        decimalScale={4}
      />
    );
  };

  AmountFormatCustom = (props: any) => {
    const { inputRef, onChange, ...other } = props;
    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef}
        onValueChange={(values: any) => {
          onChange({ target: { value: values.value } });
        }}
        allowNegative={false}
        thousandSeparator={true}
        decimalScale={2}
      />
    );
  };

  renderForm = () => {
    const config = this.state.organizationBuyoutConfig;

    return (
      <ValidatorForm onSubmit={this.saveConfig} instantValidate={true}>
        <WrapperSimple
          sectionHeading={this.translatorService.Tranlate(
            'ORGANIZATION_BUYOUT_CONFIG_SECTION',
            'Configurare despăgubire'
          )}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                name="conversionCoefficient"
                className="m-2"
                id="conversionCoefficient"
                value={config.conversionCoefficient ?? 0}
                onChange={(e: any) => this.handleConversionCoefficientChange(e.target.value)}
                label={this.translatorService.Tranlate(
                  'ORGANIZATION_BUYOUT_CONVERSION_COEFFICIENT_LABEL',
                  'Coeficient conversie'
                )}
                validators={['required', 'minNumber:0']}
                errorMessages={[
                  this.translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
                  this.translatorService.Tranlate('VALIDATORS_MIN_VALUE', 'Valoare minima 0')
                ]}
                InputProps={{ inputComponent: this.CoefficientFormatCustom }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                name="maxEligibleDamageAmount"
                className="m-2"
                id="maxEligibleDamageAmount"
                value={config.maxEligibleDamageAmount ?? ''}
                onChange={(e: any) => this.handleMaxEligibleDamageAmountChange(e.target.value)}
                label={this.translatorService.Tranlate(
                  'ORGANIZATION_BUYOUT_MAX_ELIGIBLE_DAMAGE_AMOUNT_LABEL',
                  'Prag maxim daună eligibilă (RON)'
                )}
                validators={['minNumber:0']}
                errorMessages={[
                  this.translatorService.Tranlate('VALIDATORS_MIN_VALUE', 'Valoare minima 0')
                ]}
                InputProps={{ inputComponent: this.AmountFormatCustom }}
              />
            </Grid>
            <Grid item xs={12} className="text-right">
              <Button
                className="m-2"
                variant="contained"
                color="secondary"
                type="submit"
                disabled={this.state.isSaving}
              >
                {this.translatorService.Tranlate('SAVE', 'Salveaza')}
              </Button>
            </Grid>
          </Grid>
        </WrapperSimple>
      </ValidatorForm>
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;

    return (
      <Fragment>
        <Box mt={1} pt={1}>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </div>
          {!this.state.isLoading ? this.renderForm() : null}
        </Box>
      </Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({ appState: state.app }),
  null
)(withSnackbar(OrganizationBuyoutConfigTab as any));
